import { ToastaModule } from 'ngx-toasta';
import { RouterModule } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ApplicationConfig } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';

import { jqxGridModule } from 'jqwidgets-ng/jqxgrid';
import { jqxCheckBoxModule } from 'jqwidgets-ng/jqxcheckbox';
import { jqxButtonModule } from 'jqwidgets-ng/jqxbuttons';
import { jqxWindowModule } from 'jqwidgets-ng/jqxwindow';
import { jqxInputModule } from 'jqwidgets-ng/jqxinput';
import { jqxMenuModule } from 'jqwidgets-ng/jqxmenu';
import { jqxComboBoxModule } from 'jqwidgets-ng/jqxcombobox';
import { jqxTreeModule } from 'jqwidgets-ng/jqxtree';
import { jqxTreeMapModule } from 'jqwidgets-ng/jqxtreemap';
import { jqxTabsModule } from 'jqwidgets-ng/jqxtabs';
import { jqxRadioButtonModule } from 'jqwidgets-ng/jqxradiobutton';
import { jqxDockingLayoutModule } from 'jqwidgets-ng/jqxdockinglayout';
import { jqxTreeGridModule } from 'jqwidgets-ng/jqxtreegrid';
import { jqxLayoutModule } from 'jqwidgets-ng/jqxlayout';
import { jqxRibbonModule } from 'jqwidgets-ng/jqxribbon';
import { jqxPanelModule } from 'jqwidgets-ng/jqxpanel';
import { jqxTooltipModule } from 'jqwidgets-ng/jqxtooltip';
import { jqxNotificationModule } from 'jqwidgets-ng/jqxnotification'

import { AppComponent } from './app.component';
import { HomeComponent } from './home/home.component';
import { ViewMapComponent } from './view-map/view-map.component';
import { ViewMapSearchComponent } from './components/viewMap/viewMap-search.component';
import { ViewMapProfileComponent } from './components/viewMap/viewMap-profile.component';
import { ViewMapDisclaimerComponent } from './components/viewMap/viewMap-disclaimer.component';
import { ViewMapShowReferenceLayerComponent } from './components/viewMap/viewMap-showReferenceLayer.component';
import { ViewMapGeographicalBoxComponent } from './components/viewMap/viewMap-geographicalBox.component';
import { ViewMapDateConditionComponent } from './components/viewMap/viewMap-dateCondition.component';
import { DownloadsComponent } from './downloads/downloads.component';
import { DownloadManagementComponent } from './download-management/download-management.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { LoginComponent } from './login/login.component';
import { ManagementComponent } from './management/management.component';
import { FormatTypeManagementComponent } from './components/formatType/formatType-management.component';
import { FormatTypeEditorComponent } from './components/formatType/formatType-editor.component';

import { AppRoutingModule } from './app-routes.module';


import { LocalStorageService } from './services/local-storage.service';
import { AuthService } from './services/auth.service';
import { AlertService } from './services/alert.service';
import { AuthGuardService } from './services/auth-guard.service';
import { FormatTypeService } from './services/formatType.service';
import { ViewMapService } from './services/view-map.service';
import { DownloadService } from './services/download.service';
import { RECOService } from './services/reco.service';
import { EmailService } from './services/email.service';

import { CookieService } from 'ngx-cookie-service';
import { v4 as uuidv4 } from 'uuid';

import { IPublicClientApplication, PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent } from '@azure/msal-angular';
import { msalConfig, loginRequest, protectedResources } from './auth-config';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string,
    Array<string>>();
  protectedResourceMap.set(protectedResources.todoListApi.endpoint, protectedResources.todoListApi.scopes);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest
  };
}

@NgModule({
    declarations: [
        AppComponent,
        HomeComponent,
        ViewMapSearchComponent,
        ViewMapComponent,
        ViewMapProfileComponent,
        ViewMapDisclaimerComponent,
        ViewMapShowReferenceLayerComponent,
        ViewMapGeographicalBoxComponent,
        ViewMapDateConditionComponent,
        NotFoundComponent,
        LoginComponent,
        FormatTypeManagementComponent,
        ManagementComponent,
        FormatTypeEditorComponent,
        DownloadsComponent,
        DownloadManagementComponent
    ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }), 
    FormsModule,
    ToastaModule.forRoot(),
    AppRoutingModule,
    jqxGridModule,
    jqxButtonModule,
    jqxCheckBoxModule,
    jqxInputModule,
    jqxWindowModule,
    jqxMenuModule,
    jqxComboBoxModule,
    jqxTreeModule,
    jqxTabsModule,
    jqxRadioButtonModule,
    jqxTreeMapModule,
    jqxDockingLayoutModule,
    jqxTreeGridModule,
    jqxPanelModule,
    jqxRibbonModule,
    jqxLayoutModule,
    jqxTooltipModule,
    jqxNotificationModule,
    MsalModule
  ],
  providers: [
        LocalStorageService,
        AlertService,
        AuthService,
        AuthGuardService,
        FormatTypeService,
        ViewMapService,
        RECOService,
        DownloadService,
        EmailService,
        CookieService,
        provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
        {
          provide: HTTP_INTERCEPTORS,
          useClass: MsalInterceptor,
          multi: true
        }, {
          provide: MSAL_INSTANCE,
          useFactory: MSALInstanceFactory
        }, {
          provide: MSAL_GUARD_CONFIG,
          useFactory: MSALGuardConfigFactory
        }, {
          provide: MSAL_INTERCEPTOR_CONFIG,
          useFactory: MSALInterceptorConfigFactory
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService
  ],
  bootstrap: [
    AppComponent, MsalRedirectComponent
  ]
})
export class AppModule {
  constructor(private cookieService: CookieService) {
    // Set session ID
    const sessionId = this.cookieService.get('sessionId');
    if (!sessionId)
      this.cookieService.set('sessionId', uuidv4());
  }
}
